/**
 * `ProjectModelSaveToLibrary.ts` — "Save to Library": promote a scene entity's
 * asset into the account's asset library so it can be re-placed into any
 * project.
 *
 * Unity splits this across two files. The entry point is
 * `ProjectModel_Entities.SaveEntityToAssetLbraryAsync` (typo verbatim
 * upstream), which renders the preview, decompresses cached textures, calls
 * down, and re-points the project's asset list on a fork. The real work is
 * `ProjectDataModel.SaveEntityToAssetLibraryAsync` (`ProjectDataModel.cs:1871`).
 * The web has no `ProjectDataModel` layer — its responsibilities are split
 * between the `ProjectModel` partials and `IStorageModel` — so both legs land
 * here, in their own partial rather than growing the already-large
 * `ProjectModelUpload.ts`.
 *
 * What "saving" actually means: the LIVE scene state (per-part transforms,
 * names, visibility, merge flags, materials) is baked down onto the asset's
 * PART rows, the materials are re-minted as private copies so later scene
 * edits can't mutate the library entry, and `AssetVersion` is incremented.
 * That last bit is the visibility switch — `AssetLibraryModel` hides every row
 * with `AssetVersion === 0` ("never saved to the library"), which is why a
 * web-imported asset has been invisible in the panel until now.
 *
 * Deliberate deviations from Unity, all documented in the work plan:
 *
 *  - **No texture/material DELETE sweep.** Unity collects the outgoing
 *    material + texture ids when `AssetVersion > 0` and deletes them after the
 *    copies are written. The web port already tolerates orphaned material and
 *    texture rows elsewhere (the import path leaves orphan asset rows, the
 *    asset-library delete leaves the S3 binary) because it has no
 *    admin-storage aggregate to keep in step. Deleting here without that
 *    accounting risks destroying rows another entity still references, which
 *    is strictly worse than leaving them.
 *  - **No texture decompression.** Unity's `DecompressTextures` re-encodes
 *    DXT/ETC textures out of its local GPU-format cache before upload, because
 *    "DXT or ETC textures should never appear in the database". The web never
 *    compresses: textures round-trip as the original bytes, so there is
 *    nothing to decompress.
 *  - **Fork unlinks the source asset from the scene when nothing else uses
 *    it.** Unity adds the fork to `SceneData.AssetIDs` but never removes the
 *    source, while separately calling `CustomerModel.RemoveFromCurrentProject(
 *    srcAssetId)` on the project list — the two lists disagree afterwards. The
 *    web derives the project row's `AssetIDs` as the union across scenes
 *    (`syncProjectAssetIds`), so honouring the removal intent requires doing
 *    it at the scene level. It is ref-counted rather than unconditional:
 *    another entity in the scene may still reference the source.
 */

import { randomUuid } from "@models/utils/uuid";
import { Ioc } from "@core/ioc/ioc";
import { aliasesToDto, readAliases, type PartAlias } from "@models/data/aliasData";
import { toNameFloatMap } from "@models/data/nameFloatMap";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { AssetDto } from "@models/data/assetDto";
import { assetObjectKey } from "@models/data/assetDto";
import type { EntityData, EntityTransform } from "@models/data/entityData";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import type { EntitySettingsDto } from "@models/data/entitySettingsDto";
import type { MaterialDto } from "@models/data/materialDto";
import { materialStringMap } from "@models/data/materialDto";
import { type PartDto, partMaterialIds } from "@models/data/partDto";
import { storesInAssetStorage } from "@models/storage/projectStorageV2";
import {
  libraryDiffKey,
  NOTHING_EXCLUDED,
  type LibraryDiffGroup,
  type LibrarySyncExclusions,
} from "./libraryDiff";
import { mapEntities } from "@models/data/mapEntities";
import type { IMaterialModel } from "@models/materials/IMaterialModel";
import { MATERIAL_MODEL } from "@models/materials/IMaterialModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IPreviewCaptureHost } from "@models/project/ProjectModelThumbnail";
import type { ProjectModel } from "./ProjectModel";

/** Outcome of a completed save. */

export interface SaveToLibraryResult {
  /** The asset the entity now points at — a new id when `createNewAsset`. */
  readonly assetId: string;
  /** `AssetVersion` after the increment (1 on a first save or a fork). */
  readonly assetVersion: number;
  /** True when the save forked a copy instead of updating in place. */
  readonly forked: boolean;
}

/** Thrown when the asset's licence forbids saving (Unity shows a dialog). */
export class AssetLicensingError extends Error {
  constructor() {
    super("This model is protected and may not be saved.");
    this.name = "AssetLicensingError";
  }
}

/** The slice of `AssetInfo` the Save-to-Library UI needs before it can act. */
export interface EntityAssetInfo {
  readonly assetId: string;
  /** `AssetInfo.Version` = `AssetData.AssetVersion`; `> 0` means "in library". */
  readonly version: number;
  readonly hasLicensingRestrictions: boolean;
  readonly fileName: string;
}

export class ProjectModelSaveToLibrary {
  /**
   * Read-through port of Unity's `IProjectModel.GetAsset(gameObject)`
   * (`AssetInfo` over `AssetData`). Unity can answer synchronously because the
   * whole project's asset rows are preloaded into `ProjectState`; the web port
   * loads parts lazily and never caches asset rows, so this hits Dynamo.
   *
   * Returns `null` for a media tile (no asset), an unloaded entity, or a
   * missing row.
   */
  async getEntityAssetInfoAsync(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
    signal?: AbortSignal,
  ): Promise<EntityAssetInfo | null> {
    const entity = this.getEntityById(sceneId, entityId);
    const assetId = entity?.assetId;
    if (!assetId) return null;
    const context = storageContext(this);
    if (!context) return null;
    const asset = await context.storage.getAssetById(context.customerId, assetId, signal);
    if (!asset) return null;
    return {
      assetId,
      version: typeof asset.AssetVersion === "number" ? asset.AssetVersion : 0,
      hasLicensingRestrictions: asset.HasLicensingRestrictions === true,
      fileName: asset.FileName ?? "",
    };
  }

  /**
   * Port of `ProjectModel_Entities.SaveEntityToAssetLbraryAsync` +
   * `ProjectDataModel.SaveEntityToAssetLibraryAsync`.
   *
   * @param createNewAsset Unity's "Create New" branch — fork the asset instead
   *   of updating the existing library entry. Only offered when the asset is
   *   already in the library (`AssetVersion > 0`); the caller owns that gate,
   *   exactly as `GameObjectContextViewModel.OnSaveToLibrary` does.
   */
  async saveEntityToAssetLibraryAsync(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
    createNewAsset: boolean,
    signal?: AbortSignal,
    excluded: LibrarySyncExclusions = NOTHING_EXCLUDED,
  ): Promise<SaveToLibraryResult | null> {
    const entity = this.getEntityById(sceneId, entityId);
    if (!entity) return null;
    const sourceAssetId = entity.assetId;
    if (!sourceAssetId) {
      throw new Error("Only asset-backed entities can be saved to the library.");
    }
    const context = storageContext(this);
    if (!context) {
      throw new Error("Save to Library requires the online storage context.");
    }

    const sourceAsset = await context.storage.getAssetById(context.customerId, sourceAssetId, signal);
    if (!sourceAsset) {
      // Unity: `Debug.LogWarning($"Asset {assetID} notFound"); return null;`
      console.warn(`[ProjectModel] save to library: asset row ${sourceAssetId} not found`);
      return null;
    }
    // Unity gates this in the ViewModel; the model repeats it so an AI or
    // scripted caller can't bypass the licence.
    if (sourceAsset.HasLicensingRestrictions === true) throw new AssetLicensingError();

    const [sourceParts, allSnapshots] = await Promise.all([
      context.storage.getParts(context.customerId, sourceAssetId, signal),
      context.storage.getEntitySnapshots(context.customerId, context.projectId, sceneId, undefined, signal),
    ]);
    const snapshots = allSnapshots.filter((s) => s.EntityID === entityId);
    if (snapshots.length === 0) {
      throw new Error("Save to Library failed: the entity has no snapshot rows in the cloud.");
    }

    // Render the preview from the SOURCE asset — the fork isn't in the render
    // cache yet, and Unity likewise renders `srcAssetId` before duplicating.
    const previewBytes = await capturePreview(this, sourceAssetId);

    const fork = createNewAsset
      ? forkAsset(sourceAsset, sourceParts, snapshots)
      : null;
    if (createNewAsset && !fork) {
      // Unity: `Debug.LogWarning("error in duplicating asset"); return null;`
      console.warn("[ProjectModel] save to library: asset duplication failed");
      return null;
    }

    const assetId = fork ? fork.asset.ID : sourceAssetId;
    const parts = fork ? fork.parts : sourceParts.map((p) => ({ ...p }));
    const targetSnapshots = fork ? fork.snapshots : snapshots;
    const asset: Record<string, unknown> = { ...(fork ? fork.asset : sourceAsset) };

    if (fork) {
      // Unity `CopyAndUploadAssetFiles` — the fork needs its own S3 binary.
      await copyAssetBinary(context, sourceAsset, fork.asset, signal);
    }

    // Bake the live scene state down onto the part rows, re-minting one private
    // material copy per distinct source material (Unity's `materialCopies`).
    const bake = bakeSnapshotsIntoParts(targetSnapshots, parts, excluded);
    if (!bake) {
      throw new Error("Save to Library failed: a snapshot references a part the asset doesn't have.");
    }
    if (bake.rootDisplayName !== null) asset.FileName = bake.rootDisplayName;

    const materialCopies = await copyMaterials(context, bake.materialIdMap, signal);
    const textureUploads = await copyMaterialTextures(context, materialCopies, signal);

    // ---- Writes, in Unity's order -----------------------------------------
    if (previewBytes && previewBytes.length > 0) {
      await putPreview(context, assetId, previewBytes, signal);
    } else if (fork) {
      // No viewport to render from: fall back to Unity's copy-the-cached-file
      // behaviour so the fork doesn't lose the thumbnail its source had.
      await copyAssetPreview(context, sourceAssetId, assetId, signal);
    }

    if (fork) {
      await context.storage.createAsset(context.customerId, fork.asset, signal);
      // Same SnapshotIDs, new PartIDs — these PUTs replace the entity's
      // existing snapshot rows, re-pointing it at the forked asset.
      await context.storage.createEntitySnapshots(
        context.customerId,
        context.projectId,
        sceneId,
        targetSnapshots,
        signal,
        context.serializationVersion,
      );
      if (storesInAssetStorage(context.serializationVersion)) {
        // A full-row Dynamo override for any of these ids (a desktop edit, or
        // a row written before the web wrote V2 files) wins over the file on
        // load, so it has to carry the new PartIDs too.
        await context.storage.createEntitySnapshots(
          context.customerId,
          context.projectId,
          sceneId,
          targetSnapshots,
          signal,
        );
      }
    }

    await Promise.all(textureUploads);
    if (materialCopies.length > 0) {
      await context.storage.createMaterials(context.customerId, materialCopies, signal);
    }
    await context.storage.updateParts(
      context.customerId,
      assetId,
      parts,
      signal,
      context.serializationVersion,
    );
    // Keep the in-memory mirror of the library's aliases in step with what was
    // just written. Without this the catalog still reflects the pre-save rows,
    // so placing the same asset again in this session would seed stale names
    // and the drift check would compare against a library state that no longer
    // exists.
    registerPartAliases(this, parts);
    // Same reason for the MATERIAL catalog, which additionally arms the
    // copy-on-write guard: the save just minted fresh library material rows,
    // and an edit that reached one of them would write the library entry.
    for (const part of parts) {
      if (part.ID) internals(this).registerPartMaterialIds(part.ID, partMaterialIds(part));
    }

    const assetVersion = (typeof asset.AssetVersion === "number" ? asset.AssetVersion : 0) + 1;
    asset.AssetVersion = assetVersion;
    asset.UserTag = currentNameTag();
    // `LastModified` is not set here on purpose: `assetDtoToItem` stamps it on
    // every write. It drives the library's DESC sort, its "Last Uploaded"
    // caption, and the preview cache key.
    await context.storage.updateAsset(context.customerId, asset as AssetDto, signal);

    // The saving session must not then be told its own change is available.
    // `m_assetVersionById` is the library side of that comparison and would
    // otherwise still hold the pre-save number, so switching scenes would
    // offer the user their own edit back.
    internals(this).m_assetVersionById.set(assetId, assetVersion);

    if (fork) {
      await relinkForkedAsset(this, context, sceneId, entityId, sourceAssetId, fork, signal);
    }

    // Mark the entity that pushed this version as synced to it. The fork path
    // is deliberately excluded: `relinkForkedAsset` re-points the entity at a
    // brand-new asset whose version is stamped by its own write, and the
    // snapshot passthrough would otherwise carry the SOURCE asset's watermark
    // across to it.
    if (!fork) {
      await stampSyncedVersion(this, sceneId, entityId, assetVersion, signal);
    }

    return { assetId, assetVersion, forked: fork !== null };
  }
}

// ---- Fork -----------------------------------------------------------------

interface ForkedAsset {
  readonly asset: AssetDto;
  readonly parts: PartDto[];
  readonly snapshots: EntitySnapshotDto[];
  /** old part id → new part id, for the in-memory catalog rebuild. */
  readonly partIdMap: ReadonlyMap<string, string>;
}

/**
 * Port of `ProjectDataModel_Utils.DuplicateAsset` (`:353`). A fresh asset id at
 * `AssetVersion = 0` with no variants, part rows re-minted for every part a
 * snapshot claims, and snapshot copies that keep their `SnapshotID` (so the
 * later PUT replaces the entity's rows) but point at the new part ids.
 *
 * Parts with no matching snapshot keep their original id — Unity does the same,
 * and it is safe because part rows are addressed under the asset's own sort-key
 * prefix (`#asset:{assetId}:part:{partId}`), so there is no collision with the
 * source asset's rows.
 */
function forkAsset(
  sourceAsset: AssetDto,
  sourceParts: ReadonlyArray<PartDto>,
  snapshots: ReadonlyArray<EntitySnapshotDto>,
): ForkedAsset | null {
  const assetId = randomUuid();
  const asset: AssetDto = {
    ...sourceAsset,
    ID: assetId,
    AssetVersion: 0,
    Variants: [],
    // The S3 key is derived from the id; drop any explicit override so the
    // copied binary lands at the fork's own path.
    ObjectKey: undefined,
  };

  const parts: PartDto[] = sourceParts.map((p) => ({ ...p }));
  const partsById = new Map(parts.map((p) => [p.ID, p] as const));
  const partIdMap = new Map<string, string>();
  const forkedSnapshots: EntitySnapshotDto[] = [];

  for (const snapshot of snapshots) {
    const partId = snapshot.PartID;
    const part = partId ? partsById.get(partId) : undefined;
    if (!part || !partId) {
      console.warn(`[ProjectModel] save to library: part not found ${String(partId)}`);
      return null;
    }
    let newPartId = partIdMap.get(partId);
    if (!newPartId) {
      newPartId = randomUuid();
      partIdMap.set(partId, newPartId);
      const index = parts.indexOf(part);
      parts[index] = { ...part, ID: newPartId, AssetID: assetId };
    }
    forkedSnapshots.push({ ...snapshot, PartID: newPartId });
  }

  return { asset, parts, snapshots: forkedSnapshots, partIdMap };
}

// ---- Bake -----------------------------------------------------------------

interface BakeResult {
  /** `DisplayName` of the root snapshot — becomes `AssetData.FileName`. */
  readonly rootDisplayName: string | null;
  /** source material id → freshly minted copy id. */
  readonly materialIdMap: ReadonlyMap<string, string>;
}

/**
 * Port of the per-snapshot loop in `ProjectDataModel.SaveEntityToAssetLibraryAsync`
 * (`:1934-1982`). Mutates `parts` in place.
 *
 * Note the root asymmetry, which is deliberate upstream: a NON-root snapshot's
 * live transform is written onto its part, but the ROOT's is not — the root's
 * transform is where the user happened to place the model in this scene, which
 * must not become the library pose. The root contributes only its display name,
 * which becomes the asset's file name.
 */
function bakeSnapshotsIntoParts(
  snapshots: ReadonlyArray<EntitySnapshotDto>,
  parts: PartDto[],
  excluded: LibrarySyncExclusions,
): BakeResult | null {
  const partIndexById = new Map<string, number>();
  parts.forEach((part, index) => {
    if (part.ID) partIndexById.set(part.ID, index);
  });

  const materialIdMap = new Map<string, string>();
  let rootDisplayName: string | null = null;

  for (const snapshot of snapshots) {
    const partId = snapshot.PartID;
    const index = partId ? partIndexById.get(partId) : undefined;
    if (index === undefined) {
      // Unity logs and aborts the entire save (`return null`) rather than
      // writing a half-baked asset. Same here.
      console.warn(`[ProjectModel] save to library: part not found ${String(partId)}`);
      return null;
    }

    const part = parts[index];
    const next: Record<string, unknown> = { ...part };

    // A partial save leaves the untouched groups exactly as the library holds
    // them — `next` starts as a copy of the part row, so "not selected" means
    // "not written", never "cleared". Resolved per PART: the user picks a
    // single part out of a group in the sheet.
    const writes = (group: LibraryDiffGroup): boolean =>
      !excluded.has(libraryDiffKey(partId as string, group));
    const writeOther = writes("other");
    const writeAliases = writes("aliases");
    const writeMaterials = writes("materials");

    if (snapshot.IsRoot === true) {
      rootDisplayName = snapshot.DisplayName ?? snapshot.Name ?? null;
    } else if (writeOther) {
      // Snapshot and part vectors share the Dynamo `M{x,y,z}` shape, so the
      // live pose transfers verbatim — no basis conversion on this path.
      next.Position = snapshot.Position;
      next.Rotation = snapshot.Rotation;
      next.Scale = snapshot.Scale;
    }

    if (writeOther) {
      next.Visibility = snapshot.Visibility;
      next.Merged = snapshot.HasMergedChildren;
      next.Name = snapshot.Name;
      next.DisplayName = snapshot.DisplayName;
    }
    // Part name aliases are baked like every other live field — this is what
    // makes them REUSABLE, and it is the whole point of the library leg of the
    // feature. Copied for the ROOT too, unlike the transform above: an alias
    // is a name, not a scene placement, so there is no reason to withhold it.
    // The value is carried verbatim (already normalized on write) so the
    // part row and the snapshot it came from compare equal afterwards — that
    // equality is exactly what stops the out-of-sync toast from firing against
    // the project that just did the save.
    if (writeAliases) next.Aliases = aliasesToDto(readAliases(snapshot.Aliases));

    if (writeMaterials) {
      const materialIds: string[] = [];
      for (const materialId of snapshot.MaterialIDs ?? []) {
        if (!materialId) continue;
        let copyId = materialIdMap.get(materialId);
        if (!copyId) {
          copyId = randomUuid();
          materialIdMap.set(materialId, copyId);
        }
        materialIds.push(copyId);
      }
      next.MaterialIDs = materialIds;
    }

    parts[index] = next as PartDto;
  }

  return { rootDisplayName, materialIdMap };
}

// ---- Materials & textures --------------------------------------------------

/**
 * Read every referenced material row and clone it under the minted copy id.
 *
 * Unity keys `materialCopies` by source id and stores `null` when the row is
 * missing — then dereferences `.ID` on the second reference to the same missing
 * id (an NRE) and passes the nulls to `CreateMaterials`. The web simply skips
 * unresolvable ids; the part keeps the copy id in its `MaterialIDs`, which is
 * the same end state a successful clone of a since-deleted row would produce,
 * and the renderer already tolerates a missing material row.
 */
async function copyMaterials(
  context: StorageContext,
  materialIdMap: ReadonlyMap<string, string>,
  signal?: AbortSignal,
): Promise<MaterialDto[]> {
  if (materialIdMap.size === 0) return [];
  const sourceIds = [...materialIdMap.keys()];
  const rows = await context.storage.getMaterialBatch(context.customerId, sourceIds, signal);
  const rowById = new Map(rows.map((row) => [row.ID, row] as const));

  const copies: MaterialDto[] = [];
  for (const [sourceId, copyId] of materialIdMap) {
    const row = rowById.get(sourceId);
    if (!row) {
      console.warn(`[ProjectModel] save to library: material row ${sourceId} not found — skipped`);
      continue;
    }
    copies.push({ ...row, ID: copyId });
  }
  return copies;
}

/**
 * Port of the texture-copy loop (`ProjectDataModel.cs:1988-2010`). Every
 * non-shared texture a copied material references is duplicated under a fresh
 * id so the library entry owns its own image bytes; shared library textures are
 * referenced as-is (`IsSharedTexture`).
 *
 * Mutates each copy's `Textures` map in place and returns the in-flight
 * uploads so the caller can await them alongside Unity's `Task.WhenAll`.
 */
async function copyMaterialTextures(
  context: StorageContext,
  materialCopies: MaterialDto[],
  signal?: AbortSignal,
): Promise<Array<Promise<void>>> {
  if (materialCopies.length === 0) return [];
  const materialModel = Ioc.isRegistered<IMaterialModel>(MATERIAL_MODEL)
    ? Ioc.resolve<IMaterialModel>(MATERIAL_MODEL)
    : null;

  const textureIdMap = new Map<string, string>();
  const uploads: Array<Promise<void>> = [];

  for (let i = 0; i < materialCopies.length; i++) {
    const textures = materialStringMap(materialCopies[i].Textures);
    let changed = false;
    for (const [slot, textureId] of Object.entries(textures)) {
      if (!textureId) continue;
      if (materialModel?.isSharedTexture(textureId) === true) continue;

      let copyId = textureIdMap.get(textureId);
      if (!copyId) {
        copyId = randomUuid();
        textureIdMap.set(textureId, copyId);
        uploads.push(copyTextureObject(context, textureId, copyId, signal));
      }
      textures[slot] = copyId;
      changed = true;
    }
    if (changed) materialCopies[i] = { ...materialCopies[i], Textures: textures };
  }

  return uploads;
}

async function copyTextureObject(
  context: StorageContext,
  sourceTextureId: string,
  targetTextureId: string,
  signal?: AbortSignal,
): Promise<void> {
  const url = await context.storage.getTextureBlobUrl(context.customerId, sourceTextureId, signal);
  if (!url) {
    console.warn(`[ProjectModel] save to library: texture ${sourceTextureId} missing — not copied`);
    return;
  }
  try {
    const blob = await (await fetch(url)).blob();
    await context.storage.putTextureObject(context.customerId, targetTextureId, blob, signal);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---- S3 copies -------------------------------------------------------------

/** Unity `CopyAndUploadAssetFiles` — the fork needs its own copy of the binary. */
async function copyAssetBinary(
  context: StorageContext,
  sourceAsset: AssetDto,
  targetAsset: AssetDto,
  signal?: AbortSignal,
): Promise<void> {
  const sourceKey = assetObjectKey(sourceAsset);
  const targetKey = assetObjectKey(targetAsset);
  const source = await context.storage.getAssetBlobUrl(context.customerId, sourceKey, signal);
  if (!source) {
    throw new Error(`Save to Library failed: the asset binary ${sourceKey} could not be read.`);
  }
  try {
    const blob = await (await fetch(source.url)).blob();
    await context.storage.putAssetObject(context.customerId, targetKey, blob, signal);
  } finally {
    URL.revokeObjectURL(source.url);
  }
}

async function copyAssetPreview(
  context: StorageContext,
  sourceAssetId: string,
  targetAssetId: string,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const url = await context.storage.getAssetPreviewBlobUrl(context.customerId, sourceAssetId, signal);
    if (!url) return;
    try {
      const bytes = new Uint8Array(await (await fetch(url)).arrayBuffer());
      await context.storage.putAssetPreviewObject(context.customerId, targetAssetId, bytes, signal);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.warn("[ProjectModel] save to library: preview copy failed:", err);
  }
}

async function putPreview(
  context: StorageContext,
  assetId: string,
  bytes: Uint8Array,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await context.storage.putAssetPreviewObject(context.customerId, assetId, bytes, signal);
  } catch (err) {
    // Same non-fatal stance as the import path: a missing thumbnail must not
    // fail a save whose Dynamo rows are already partly written.
    console.warn(`[ProjectModel] save to library: preview upload failed (asset=${assetId}):`, err);
  }
}

async function capturePreview(model: ProjectModel, assetId: string): Promise<Uint8Array | null> {
  const host = (model as unknown as { m_previewCaptureHost: IPreviewCaptureHost | null })
    .m_previewCaptureHost;
  if (!host) return null;
  try {
    return await host.captureAssetPreviewAsync(assetId);
  } catch (err) {
    console.warn(`[ProjectModel] save to library: preview capture failed (asset=${assetId}):`, err);
    return null;
  }
}

// ---- In-memory re-link after a fork ---------------------------------------

/**
 * Re-point the local model at the forked asset: register the new part catalog,
 * rebuild the entity from the forked snapshot rows, swap the scene's `AssetIDs`
 * (ref-counted — see the file header), and re-derive the project row's list.
 */
async function relinkForkedAsset(
  model: ProjectModel,
  context: StorageContext,
  sceneId: string,
  entityId: string,
  sourceAssetId: string,
  fork: ForkedAsset,
  signal?: AbortSignal,
): Promise<void> {
  const state = internals(model);
  registerParts(model, fork.asset.ID, fork.parts);

  // The entity's settings row is untouched by a fork (same `EntityID`), but
  // `mapEntities` needs it to rebuild the entity — same re-fetch
  // `duplicateEntityAsync` does.
  let settings: EntitySettingsDto = { EntityID: entityId };
  try {
    const all = await context.storage.getEntitySettings(
      context.customerId,
      context.projectId,
      sceneId,
      signal,
    );
    settings = all.find((s) => s.EntityID === entityId) ?? settings;
  } catch (err) {
    console.warn("[ProjectModel] save to library: entity settings re-fetch failed:", err);
  }

  const [entity] = mapEntities({
    sceneId,
    snapshots: fork.snapshots,
    settings: [settings],
    partIdToAssetId: state.m_partIdToAssetId,
    partHierarchyById: state.m_partHierarchyById,
    partTransformById: state.m_partTransformById,
  });

  const list = state.m_entitiesByScene.get(sceneId) ?? [];
  state.m_entitiesByScene.set(
    sceneId,
    entity ? list.map((e) => (e.id === entityId ? entity : e)) : list,
  );

  // Scene asset list: add the fork, drop the source when nothing else in the
  // scene still points at it.
  const sceneAssets = new Set(state.m_assetIdsByScene.get(sceneId) ?? []);
  sceneAssets.add(fork.asset.ID);
  const stillUsed = (state.m_entitiesByScene.get(sceneId) ?? []).some(
    (e) => e.assetId === sourceAssetId,
  );
  if (!stillUsed) sceneAssets.delete(sourceAssetId);
  state.m_assetIdsByScene.set(sceneId, [...sceneAssets]);

  const scene = model.getScene(sceneId);
  if (scene) model.applySceneChanges({ ...scene, assetIds: [...sceneAssets] });

  model.onEntityChangesApplied.emit({ sceneId });

  try {
    await context.storage.updateSceneAssetIDs(
      context.customerId,
      context.projectId,
      sceneId,
      [...sceneAssets],
      signal,
    );
    await syncProjectAssetIds(model, signal);
  } catch (err) {
    console.warn("[ProjectModel] save to library: asset-list sync failed:", err);
  }
}

// ---- Shared plumbing -------------------------------------------------------

interface StorageContext {
  storage: IStorageModel;
  customerId: string;
  projectId: string;
  serializationVersion: number | undefined;
}

interface SaveInternals {
  readonly m_entitiesByScene: Map<string, ReadonlyArray<EntityData>>;
  readonly m_assetIdsByScene: Map<string, ReadonlyArray<string>>;
  readonly m_partIdToAssetId: Map<string, string>;
  readonly m_partHierarchyById: Map<string, string>;
  readonly m_partTransformById: Map<string, EntityTransform>;
  readonly m_partAliasesById: Map<string, ReadonlyArray<PartAlias>>;
  readonly m_partsLoadedAssets: Set<string>;
  readonly m_assetVersionById: Map<string, number>;
  /** Single writer for the library-material catalog + its reverse index. */
  registerPartMaterialIds(partId: string, materialIds: ReadonlyArray<string>): void;
}

function internals(model: ProjectModel): SaveInternals {
  return model as unknown as SaveInternals;
}

function storageContext(model: ProjectModel): StorageContext | null {
  return (
    model as unknown as { getStorageContext(): StorageContext | null }
  ).getStorageContext();
}

function registerParts(
  model: ProjectModel,
  assetId: string,
  parts: ReadonlyArray<PartDto>,
): void {
  const state = internals(model);
  for (const part of parts) {
    if (!part.ID) continue;
    state.m_partIdToAssetId.set(part.ID, assetId);
    if (part.HierachyID) state.m_partHierarchyById.set(part.ID, part.HierachyID);
    state.m_partTransformById.set(part.ID, {
      position: vec3Of(part.Position),
      rotation: vec3Of(part.Rotation),
      scale: vec3Of(part.Scale, [1, 1, 1]),
    });
    // See the twin in `ProjectModelUpload.registerParts`: this is what keeps
    // the copy-on-write guard armed for an asset registered outside scene load.
    state.registerPartMaterialIds(part.ID, partMaterialIds(part));
  }
  registerPartAliases(model, parts);
  state.m_partsLoadedAssets.add(assetId);
}

/**
 * Mirror each part row's library aliases into the catalog used by the drift
 * check. DELETES the entry for a part whose aliases were cleared, so removal
 * in the library is reflected rather than leaving a stale list behind — the
 * map's membership is what marks an asset as "library-aliased".
 */
/**
 * Record that this entity is synced to the version it just published, so the
 * library-update prompt does not offer the author their own save.
 * Best-effort: a failed stamp only costs one spurious prompt.
 */
async function stampSyncedVersion(
  model: ProjectModel,
  sceneId: string,
  entityId: string,
  assetVersion: number,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const entity = model.getEntityById(sceneId, entityId);
    const root = entity?.snapshots.find((s) => s.isRoot) ?? entity?.snapshots[0];
    if (!root) return;
    await model.updateEntitySnapshotAsync(
      sceneId,
      root.id,
      { librarySyncedVersion: assetVersion },
      signal,
    );
  } catch (err) {
    console.warn("[ProjectModel] save to library: could not stamp the synced version:", err);
  }
}

function registerPartAliases(model: ProjectModel, parts: ReadonlyArray<PartDto>): void {
  const state = internals(model);
  for (const part of parts) {
    if (!part.ID) continue;
    const aliases = readAliases(part.Aliases);
    if (aliases.length > 0) state.m_partAliasesById.set(part.ID, aliases);
    else state.m_partAliasesById.delete(part.ID);
  }
}

async function syncProjectAssetIds(
  model: ProjectModel,
  signal?: AbortSignal,
): Promise<void> {
  await model.syncProjectAssetIdsAsync(signal);
}

function currentNameTag(): string {
  // Unity passes `m_authModel.CurrentUser.NameTag` down from
  // `ProjectModel_Entities`; `ProjectModel` resolves auth from IOC on demand
  // rather than holding a field, so this follows the same pattern.
  if (!Ioc.isRegistered<IAuthModel>(AUTH_MODEL)) return "";
  return Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.nameTag ?? "";
}

function vec3Of(
  value: unknown,
  fallback: readonly [number, number, number] = [0, 0, 0],
): EntityTransform["position"] {
  // `toNameFloatMap`, not a cast: a V2 part row carries its vectors as
  // `NameFloatPair[]`, which a cast reads as `{}` and silently defaults.
  const map = toNameFloatMap(value);
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  return [num(map.x, fallback[0]), num(map.y, fallback[1]), num(map.z, fallback[2])];
}
